import React from 'react';
import LicenseGate from '../components/ui/LicenseGate';
import DashboardSectionAnchor from '../components/ui/DashboardSectionAnchor';
import { useClubConditionsData } from '../hooks/useClubConditionsQueries';
import { useBranches } from '../hooks/useBranches';
import { TimePeriod } from '../types/api';
import {
  ClubConditionsTable,
  UsedCreditRatioChart,
  GeneratedCreditRatioChart,
  GeneratedCreditAmountRatioChart,
  ParticipationRateChart,
  ParticipationAmountChart,
  HorizontalRatioBarChart
} from '../components/clubConditions';
import { ClubConditionsData } from '../types/clubConditions';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ErrorMessage from '../components/ui/ErrorMessage';

interface ClubConditionsDashboardProps {
  branchId?: number;
  customTimeRange?: TimePeriod;
}

// آزمایشی: پیکربندی ۵ متریک برای نمودارهای میله‌ای افقی زیر جدول اصلی
const HORIZONTAL_CHART_CONFIGS: Array<{
  id: string;
  title: string;
  description: string;
  getValue: (item: ClubConditionsData) => number;
}> = [
  {
    id: 'used-credit-ratio',
    title: 'میزان اعتبار مصرف شده به فروش',
    description: 'نسبت اعتبار مصرف شده به فروش کل در بازه زمانی',
    getValue: (item) => (item.usedToAllSales || 0) * 100
  },
  {
    id: 'generated-credit-ratio',
    title: 'تعداد اعتبار تولیدی به تعداد سفارش',
    description: 'نسبت تعداد اعتبار تولید شده به تعداد فاکتور',
    getValue: (item) => (item.countCreditToCountFacture || 0) * 100
  },
  {
    id: 'generated-credit-amount-ratio',
    title: 'مبلغ اعتبار تولیدی به مبلغ سفارش',
    description: 'نسبت مبلغ اعتبار تولید شده به مبلغ سفارش',
    getValue: (item) =>
      item.factureSum > 0 ? (item.generatedCreditSum / item.factureSum) * 100 : 0
  },
  {
    id: 'participation-rate',
    title: 'نرخ تعدادی مصرف اعتبار در هر ماه',
    description: 'درصد مشارکت تعدادی شروط در بازه زمانی',
    getValue: (item) => (item.subCountInMonth || 0) * 100
  },
  {
    id: 'participation-amount',
    title: 'درصد مشارکت مبلغی شروط',
    description: 'درصد مشارکت مبلغی شروط در بازه زمانی',
    getValue: (item) => (item.subCreditInMonth || 0) * 100
  }
];

const ClubConditionsDashboard: React.FC<ClubConditionsDashboardProps> = ({
  branchId = 0, // Default to اندرزگو branch
  customTimeRange
}) => {
  // Fetch branch info
  const { branchName } = useBranches(branchId);
  
  // Fetch club conditions data
  const { data, isLoading, error } = useClubConditionsData(branchId, customTimeRange);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <ErrorMessage message="بارگذاری داده‌ها ناموفق بود؛ لطفاً دوباره تلاش کنید." />
      </div>
    );
  }

  const clubConditionsData = data?.data || [];
  const currencySymbol = data?.currencyMetadata?.currencySymbol;
  
  if (!currencySymbol && clubConditionsData.length > 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <ErrorMessage message="خطا در دریافت واحد پولی" />
      </div>
    );
  }

  return (
    <LicenseGate licenseType="club" dashboardName="داشبورد شروط باشگاه مشتریان">
      <div dir="rtl" className="container-dashboard">
        <div className="space-y-8 md:space-y-10 animate-enter-up">
          {/* Header */}
          <h1 className="title-dashboard">
            شروط باشگاه مشتریان
          </h1>

          {/* Table Section */}
          <DashboardSectionAnchor sectionId="club-conditions-table" className="animate-scale-in stagger-1">
            <ClubConditionsTable 
              data={clubConditionsData}
              currencySymbol={currencySymbol}
              branchId={branchId}
              timeRange={customTimeRange}
            />
          </DashboardSectionAnchor>

          {/* Test Section - Horizontal Bar Charts */}
          {/* <div className="animate-scale-in stagger-2 space-y-6">
            <h2 className="title-card text-right">
              نمای آزمایشی — نمودارهای میله‌ای افقی
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {HORIZONTAL_CHART_CONFIGS.map((config, index) => {
                const isLast = index === HORIZONTAL_CHART_CONFIGS.length - 1;
                const isOddLast = isLast && HORIZONTAL_CHART_CONFIGS.length % 2 === 1;
                return (
                  <div key={config.id} className={isOddLast ? 'lg:col-span-2' : ''}>
                    <HorizontalRatioBarChart
                      data={clubConditionsData}
                      title={config.title}
                      description={config.description}
                      getValue={config.getValue}
                      colorIndex={index}
                    />
                  </div>
                );
              })}
            </div>
          </div> */}

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-scale-in stagger-2">
            {/* Row 1 */}
            <div className="animate-scale-in stagger-3">
            <DashboardSectionAnchor sectionId="generated-credit-ratio-chart">
            <GeneratedCreditRatioChart 
                data={clubConditionsData} 
                isLoading={isLoading}
                error={error}
                branchId={branchId}
                branchName={branchName}
                timeRange={customTimeRange}
              />
            </DashboardSectionAnchor>
            </div>
            <div className="animate-scale-in stagger-4">
              <DashboardSectionAnchor sectionId="generated-credit-amount-ratio-chart">
              <GeneratedCreditAmountRatioChart 
                data={clubConditionsData} 
                isLoading={isLoading}
                error={error}
                branchId={branchId}
                branchName={branchName}
                timeRange={customTimeRange}
              />
              </DashboardSectionAnchor>
            </div>

            {/* Row 2 */}
            <div className="animate-scale-in stagger-5">
            <DashboardSectionAnchor sectionId="participation-amount-chart">
            <ParticipationAmountChart 
                data={clubConditionsData} 
                isLoading={isLoading}
                error={error}
                branchId={branchId}
                branchName={branchName}
                timeRange={customTimeRange}
              />
            </DashboardSectionAnchor>
            </div>
            <div className="animate-scale-in stagger-6">
              <DashboardSectionAnchor sectionId="participation-rate-chart">
              <ParticipationRateChart 
                data={clubConditionsData} 
                isLoading={isLoading}
                error={error}
                branchId={branchId}
                branchName={branchName}
                timeRange={customTimeRange}
              />
              </DashboardSectionAnchor>
            </div>

            {/* Row 3 - Full width */}
            <div className="lg:col-span-2 animate-scale-in stagger-7">
            <DashboardSectionAnchor sectionId="used-credit-ratio-chart">
            <UsedCreditRatioChart 
                data={clubConditionsData} 
                isLoading={isLoading}
                error={error}
                branchId={branchId}
                branchName={branchName}
                timeRange={customTimeRange}
              />
            </DashboardSectionAnchor>
            </div>
          </div>
        </div>
      </div>
    </LicenseGate>
  );
};

export default ClubConditionsDashboard;